import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate, statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { Wrench, Plus, X } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Maintenance() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const reload = async () => {
    const [t, u] = await Promise.all([
      api.get("/maintenance"),
      api.get("/users").catch(()=>({data:[]})),
    ]);
    setTickets(t.data);
    setStaff(u.data.filter(x => x.role === "maintenance"));
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/maintenance", {
        title: f.get("title"),
        description: f.get("description"),
        category: f.get("category"),
        priority: f.get("priority"),
      });
      toast.success("Ticket submitted");
      setCreating(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const assign = async (id, userId) => {
    try { await api.post(`/maintenance/${id}/assign`, { user_id: userId }); toast.success("Assigned"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const update = async (id, payload) => {
    try { await api.patch(`/maintenance/${id}`, payload); toast.success("Updated"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const rate = async (id) => {
    const r = prompt("Rate 1-5:");
    if (!r) return;
    await update(id, { tenant_rating: Number(r) });
  };

  if (loading) return <Loading/>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Maintenance Tickets</h1>
          <p className="text-[#5E7166] text-sm">{tickets.length} tickets.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="new-ticket-btn"><Plus size={16}/> New Ticket</button>
      </div>

      <Card className="!p-0 overflow-hidden">
        {tickets.length === 0 ? <div className="p-8 text-center text-[#5E7166]">No tickets yet.</div> :
          <div className="overflow-x-auto">
            <table className="modav-table">
              <thead><tr><th>Title</th><th>Unit</th><th>Priority</th><th>Status</th><th>Created</th><th>Rating</th><th></th></tr></thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.id} data-testid={`ticket-row-${t.id}`}>
                    <td><div className="font-semibold">{t.title}</div><div className="text-xs text-[#5E7166] line-clamp-1">{t.description}</div></td>
                    <td>{t.unit?.unit_name || "—"}</td>
                    <td><span className={statusBadgeClass(t.priority)}>{statusLabel(t.priority)}</span></td>
                    <td><span className={statusBadgeClass(t.status)}>{statusLabel(t.status)}</span></td>
                    <td className="text-xs">{formatDate(t.created_at)}</td>
                    <td>{t.tenant_rating ? `${"★".repeat(t.tenant_rating)}` : "—"}</td>
                    <td>
                      {["admin","manager"].includes(user.role) && t.status === "open" && (
                        <select onChange={(e) => e.target.value && assign(t.id, e.target.value)} className="text-xs border border-[#E5EBE8] rounded p-1" data-testid={`assign-${t.id}`}>
                          <option value="">Assign…</option>
                          {staff.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                        </select>
                      )}
                      {user.role === "tenant" && t.status === "completed" && !t.tenant_rating && (
                        <button onClick={() => rate(t.id)} className="btn-outline-emerald text-xs">Rate</button>
                      )}
                      {["admin","manager"].includes(user.role) && t.status !== "closed" && (
                        <button onClick={() => update(t.id, { status: "closed" })} className="btn-ghost text-xs ml-1">Close</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="ticket-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">New Maintenance Ticket</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <input name="title" required placeholder="Brief title" className="input-modav" data-testid="ticket-title"/>
              <textarea name="description" required placeholder="Describe the issue" className="input-modav" rows={4} data-testid="ticket-description"/>
              <div className="grid grid-cols-2 gap-3">
                <select name="category" className="input-modav">
                  <option value="plumbing">Plumbing</option><option value="electrical">Electrical</option>
                  <option value="cleaning">Cleaning</option><option value="repair">Repair</option><option value="other">Other</option>
                </select>
                <select name="priority" defaultValue="medium" className="input-modav" data-testid="ticket-priority">
                  <option value="low">Low</option><option value="medium">Medium</option>
                  <option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="submit-ticket-btn">Submit</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
